/**
 * `UiSoundService` — short interface sounds, synthesized with Web Audio. No
 * audio files are shipped or fetched.
 *
 * **Web-only — there is no Unity counterpart.** The desktop client plays no
 * interface sounds beyond `FloatingClickable`'s click. The synthesis is ported
 * from Alex's design kit (alexdevkit v2, `kit/sound/sound.ts`; user direction,
 * 2026-10-28), and so is its rule: a sound marks something the user did or
 * something that arrived, never a hover. Clicks are filtered noise, because a
 * sine click sounds like a beep, and tones mark a state change.
 *
 * `listen` and `expire` are shortened versions of the OpenHome prototype's
 * `muteUp` / `muteDown` instead (`prototype/src/audio/FeedbackSounds.js`; user
 * direction, 2026-10-28: "similar to this but shorter").
 *
 * The cue bank holds only the cues something plays. Add a cue when a caller
 * needs one, not before.
 *
 * ⚠ **Never set `navigator.audioSession.type` here.** The kit sets it to
 * `ambient`, which is correct for a page that only plays sounds. This page
 * also records Spark's microphone, and on iOS Safari an `ambient` session
 * cannot record.
 */

/**
 * - `on` / `off` — something switched on or off (the microphone).
 * - `enter` / `leave` — a mode began or ended (voice mode).
 * - `send` — the user's message left.
 * - `arrive` — a message arrived.
 * - `listen` / `expire` — the mic opened for one reply, or closed because it
 *   heard nothing (push-to-talk).
 */
export type UiSoundCue =
  | "on"
  | "off"
  | "enter"
  | "leave"
  | "send"
  | "arrive"
  | "listen"
  | "expire";

export interface IUiSoundService {
  /** Plays one cue now. Never throws, and stays silent where the browser has no Web Audio. */
  play(cue: UiSoundCue): void;
}

export const UI_SOUND_SERVICE = Symbol.for("Campfire.UiSoundService");

/** Output level of every cue together. The kit's value. */
const K_MASTER_GAIN = 0.28;
/** Length of the shared white-noise buffer the clicks and sweeps are filtered from. */
const K_NOISE_SECONDS = 0.5;
/**
 * The longest a cue may wait for the browser to allow audio. A suspended
 * context starts only after a user gesture. A cue still waiting after this long
 * is dropped, so it does not play later at an unrelated moment.
 */
const K_MAX_CUE_DELAY_MS = 250;

export class UiSoundService implements IUiSoundService {
  private m_context: AudioContext | null = null;
  private m_output: GainNode | null = null;
  private m_noise: AudioBuffer | null = null;

  play(cue: UiSoundCue): void {
    const context = this.ensureContext();
    if (!context) return;
    if (context.state === "running") {
      this.playCue(cue);
      return;
    }
    // The context is suspended until a user gesture allows audio, or
    // interrupted on iOS. Ask for it back, and play only if that happens soon.
    const requestedAt = performance.now();
    context.resume().then(
      () => {
        if (performance.now() - requestedAt <= K_MAX_CUE_DELAY_MS) this.playCue(cue);
      },
      () => undefined,
    );
  }

  /** Created on the first cue rather than at boot, so the first cue usually comes from a gesture. */
  private ensureContext(): AudioContext | null {
    if (this.m_context) return this.m_context;
    if (typeof AudioContext === "undefined") return null;
    try {
      const context = new AudioContext({ latencyHint: "interactive" });
      const output = context.createGain();
      output.gain.value = K_MASTER_GAIN;
      output.connect(context.destination);
      const noise = context.createBuffer(1, context.sampleRate * K_NOISE_SECONDS, context.sampleRate);
      const samples = noise.getChannelData(0);
      for (let i = 0; i < samples.length; i++) samples[i] = Math.random() * 2 - 1;
      this.m_context = context;
      this.m_output = output;
      this.m_noise = noise;
      return context;
    } catch {
      return null;
    }
  }

  private playCue(cue: UiSoundCue): void {
    try {
      switch (cue) {
        case "on":
          this.noise(0.008, 3000, 2500, 0.12, 2);
          this.tone(660, 680, 0.06, 0.1);
          this.tone(990, 1000, 0.09, 0.08, "sine", 0.045);
          break;
        case "off":
          this.noise(0.008, 2600, 2000, 0.12, 2);
          this.tone(740, 720, 0.06, 0.09);
          this.tone(495, 490, 0.09, 0.08, "sine", 0.045);
          break;
        case "enter":
          // The kit's `open` sweep under its `join` bells.
          this.noise(0.22, 600, 2400, 0.04, 0.8);
          this.bell(659, 0.06);
          this.bell(988, 0.05, 0.09);
          break;
        case "leave":
          // The same bells falling, over the kit's `close` sweep.
          this.noise(0.16, 2000, 700, 0.035, 0.8);
          this.bell(988, 0.05);
          this.bell(659, 0.045, 0.09);
          break;
        case "send":
          this.tone(520, 1040, 0.11, 0.09);
          this.noise(0.14, 1500, 4200, 0.03);
          break;
        case "arrive":
          this.bell(1175, 0.07);
          this.bell(1568, 0.05, 0.07);
          break;
        // OpenHome's mute glides, shortened: same pitches, partials and
        // level ratios, with the glide and decay cut to about 40% (audible
        // ~0.13 s instead of ~0.3 s). The levels are scaled so the peak sits
        // with the cues above (about −31 dBFS) instead of OpenHome's −28.
        case "listen":
          this.glide(392, 740, 0.08, 0.1, 0.006, 0.055, 0.4);
          this.glide(784, 1480, 0.08, 0.015, 0.005, 0.03, 0.25);
          break;
        case "expire":
          this.glide(740, 392, 0.09, 0.113, 0.006, 0.06, 0.4);
          this.glide(1480, 784, 0.09, 0.017, 0.005, 0.035, 0.25);
          break;
      }
    } catch {
      // A node the browser refused to build costs one cue, nothing else.
    }
  }

  /** One oscillator gliding from `f0` to `f1` Hz under a 3 ms attack and an exponential decay. */
  private tone(
    f0: number,
    f1: number,
    duration: number,
    gain: number,
    type: OscillatorType = "sine",
    delay = 0,
  ): void {
    const context = this.m_context!;
    const start = context.currentTime + delay;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(f0, start);
    oscillator.frequency.exponentialRampToValueAtTime(f1, start + duration);
    envelope.gain.setValueAtTime(0.0001, start);
    envelope.gain.exponentialRampToValueAtTime(gain, start + 0.003);
    envelope.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(envelope).connect(this.m_output!);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  }

  /** White noise through a band-pass whose centre sweeps from `f0` to `f1` Hz. */
  private noise(
    duration: number,
    f0: number,
    f1: number,
    gain: number,
    q = 1.2,
    delay = 0,
  ): void {
    const context = this.m_context!;
    const start = context.currentTime + delay;
    const source = context.createBufferSource();
    const bandPass = context.createBiquadFilter();
    const envelope = context.createGain();
    source.buffer = this.m_noise;
    bandPass.type = "bandpass";
    bandPass.Q.value = q;
    bandPass.frequency.setValueAtTime(f0, start);
    bandPass.frequency.exponentialRampToValueAtTime(f1, start + duration);
    envelope.gain.setValueAtTime(0.0001, start);
    envelope.gain.exponentialRampToValueAtTime(gain, start + Math.min(0.004, duration * 0.3));
    envelope.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    source.connect(bandPass).connect(envelope).connect(this.m_output!);
    source.start(start);
    source.stop(start + duration + 0.02);
  }

  /**
   * OpenHome's tone: a sine gliding from `f0` to `f1` Hz over `glideTime`,
   * with a linear `attack` to `peak` and then a decay of time constant `tau`.
   * It stops at `duration`, by which point it is silent.
   */
  private glide(
    f0: number,
    f1: number,
    glideTime: number,
    peak: number,
    attack: number,
    tau: number,
    duration: number,
  ): void {
    const context = this.m_context!;
    const start = context.currentTime;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.frequency.setValueAtTime(f0, start);
    oscillator.frequency.exponentialRampToValueAtTime(f1, start + glideTime);
    envelope.gain.setValueAtTime(0, start);
    envelope.gain.linearRampToValueAtTime(peak, start + attack);
    envelope.gain.setTargetAtTime(0, start + attack, tau);
    oscillator.connect(envelope).connect(this.m_output!);
    oscillator.start(start);
    oscillator.stop(start + duration);
  }

  /** A struck bell: partials 1, 2 and 3.01, each quieter and shorter than the last. */
  private bell(frequency: number, gain: number, delay = 0): void {
    this.tone(frequency, frequency, 0.5, gain, "sine", delay);
    this.tone(frequency * 2, frequency * 2, 0.3, gain * 0.22, "sine", delay);
    this.tone(frequency * 3.01, frequency * 3.01, 0.2, gain * 0.08, "sine", delay);
  }
}
